import * as yup from "yup";
import type { ValidationError } from "yup";
import { isValidPhoneNumber } from "libphonenumber-js";

// Regex patterns
const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
const personNameRegex = /^[a-zA-Z\s'\u00C0-\u024F-]+$/;

/** Removes characters that are not allowed in a person name (for live input). */
export function stripDisallowedPersonNameChars(input: string): string {
  return input.replace(/[^a-zA-Z\s'\u00C0-\u024F-]/g, "");
}
const passwordRegex =
  /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*#?&])[A-Za-z\d@$!%*#?&]/;

const MIN_SIGNUP_AGE = 16;

// Error messages
export const errorMessages = {
  email: {
    required: "Email address is required",
    invalid: "Please enter a valid email address",
    format: "Email should be in format: example@domain.com",
  },
  name: {
    required: "Name is required",
    invalidFormat:
      "Name can only contain letters, spaces, hyphens, and apostrophes",
  },
  phoneNumber: {
    required: "Phone number is required",
    invalid: "Please enter a valid phone number",
  },
  dateOfBirth: {
    required: "Date of birth is required",
  },
  ageConfirmed: {
    required: "Please confirm you are 16 or older",
  },
  password: {
    required: "Password is required",
    minLength: "Password must be at least 8 characters long",
    maxLength: "Password cannot exceed 128 characters",
    strength:
      "Password must contain at least one uppercase letter, one lowercase letter, one number, and one special character (@$!%*?&)",
    common: "Please choose a stronger password",
  },
  confirmPassword: {
    required: "Please confirm your password",
    mismatch: "Passwords do not match",
  },
};

// Common password list
const commonPasswords = [
  "password",
  "123456",
  "123456789",
  "qwerty",
  "abc123",
  "password123",
  "admin",
  "letmein",
  "welcome",
  "monkey",
  "1234567890",
  "12345678",
  "1234567",
  "12345678910",
];

const passwordValidation = yup
  .string()
  .required(errorMessages.password.required)
  .min(8, errorMessages.password.minLength)
  .max(128, errorMessages.password.maxLength)
  .matches(passwordRegex, errorMessages.password.strength)
  .test("not-common", errorMessages.password.common, (value) => {
    if (!value) return true;
    return !commonPasswords.includes(value.toLowerCase());
  });

const emailValidation = yup
  .string()
  .trim()
  .required(errorMessages.email.required)
  .matches(emailRegex, errorMessages.email.invalid)
  .email(errorMessages.email.format)
  .max(254, "Email address is too long");

const phoneNumberValidation = yup
  .string()
  .trim()
  .required(errorMessages.phoneNumber.required)
  .test(
    "valid-phone",
    errorMessages.phoneNumber.invalid,
    (value) => Boolean(value && isValidPhoneNumber(value))
  );

const optionalPhoneNumberValidation = yup
  .string()
  .trim()
  .test(
    "valid-phone",
    errorMessages.phoneNumber.invalid,
    (value) => {
      if (!value) {
        return true;
      }
      return isValidPhoneNumber(value);
    }
  );

// Schemas
export const loginSchema = yup.object().shape({
  email: emailValidation,
  password: yup
    .string()
    .required(errorMessages.password.required)
    .min(8, errorMessages.password.required)
    .max(128, errorMessages.password.maxLength),
});

export const adminInviteSchema = yup.object().shape({
  email: emailValidation,
  name: yup
    .string()
    .trim()
    .required(errorMessages.name.required)
    .max(120, "Name is too long"),
});

export const adminFirstLoginPasswordSchema = yup.object().shape({
  password: passwordValidation,
  confirmPassword: yup
    .string()
    .required(errorMessages.confirmPassword.required)
    .oneOf([yup.ref("password")], errorMessages.confirmPassword.mismatch),
});

function isValidMinAgeDob(value: string): boolean {
  if (!/^\d{2}\/\d{2}\/\d{4}$/.test(value)) {
    return false;
  }
  const [day, month, year] = value.split("/");
  const dob = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
  if (isNaN(dob.getTime())) {
    return false;
  }
  const today = new Date();
  const minAgeCutoff = new Date(
    today.getFullYear() - MIN_SIGNUP_AGE,
    today.getMonth(),
    today.getDate()
  );
  return dob <= minAgeCutoff;
}

// DOB validation schema - might need in future
// const dateOfBirthValidation = yup
//   .string()
//   .required(errorMessages.dateOfBirth.required)
//   .test(
//     "valid-format",
//     "Date of birth must be in DD/MM/YYYY format",
//     (value) => {
//       if (!value) return false;
//       return /^\d{2}\/\d{2}\/\d{4}$/.test(value);
//     }
//   )
//   .test(
//     "min-signup-age",
//     `You must be at least ${MIN_SIGNUP_AGE} years old`,
//     function (value) {
//       if (!value) return false;
//       return isValidMinAgeDob(value);
//     }
//   );

const optionalDateOfBirthValidation = yup
  .string()
  .trim()
  .test(
    "valid-format",
    "Date of birth must be in DD/MM/YYYY format",
    (value) => {
      if (!value) return true;
      return /^\d{2}\/\d{2}\/\d{4}$/.test(value);
    }
  )
  .test(
    "min-signup-age",
    `You must be at least ${MIN_SIGNUP_AGE} years old`,
    (value) => {
      if (!value) return true;
      return isValidMinAgeDob(value);
    }
  );

export const onboardingDobSchema = yup.object().shape({
  dateOfBirth: optionalDateOfBirthValidation,
  ageConfirmed: yup.boolean().default(false),
}).test(
  "age-or-dob",
  errorMessages.ageConfirmed.required,
  function (values) {
    const dob = values?.dateOfBirth?.trim();
    if (values?.ageConfirmed) {
      return true;
    }
    if (dob && isValidMinAgeDob(dob)) {
      return true;
    }
    return this.createError({
      path: "ageConfirmed",
      message: errorMessages.ageConfirmed.required,
    });
  }
);

export const signupSchema = yup.object().shape({
  name: yup
    .string()
    .trim()
    .required(errorMessages.name.required)
    .max(254, "Name is too long")
    .matches(personNameRegex, errorMessages.name.invalidFormat),
  email: emailValidation,
  phoneNumber: optionalPhoneNumberValidation,
  dateOfBirth: optionalDateOfBirthValidation,
  ageConfirmed: yup.boolean().default(false),
  password: passwordValidation,
  confirmPassword: yup
    .string()
    .required(errorMessages.confirmPassword.required)
    .oneOf([yup.ref("password")], errorMessages.confirmPassword.mismatch),
}).test(
  "age-or-dob",
  errorMessages.ageConfirmed.required,
  function (values) {
    const dob = values?.dateOfBirth?.trim();
    if (values?.ageConfirmed) {
      return true;
    }
    if (dob && isValidMinAgeDob(dob)) {
      return true;
    }
    return this.createError({
      path: "ageConfirmed",
      message: errorMessages.ageConfirmed.required,
    });
  }
);
export const getFieldValidation = (fieldName: string) => {
  const validations: Record<string, yup.StringSchema> = {
    email: emailValidation,
    password: passwordValidation,
    confirmPassword: yup
      .string()
      .required(errorMessages.confirmPassword.required)
      .oneOf([yup.ref("password")], errorMessages.confirmPassword.mismatch),
  };
  return validations[fieldName] || yup.string();
};

export const changePasswordSchema = yup.object().shape({
  oldPassword: yup
    .string()
    .required("Old password is required")
    .min(8, errorMessages.password.minLength)
    .max(128, errorMessages.password.maxLength),
  password: passwordValidation.notOneOf(
    [yup.ref("oldPassword")],
    "New password cannot be the same as your current password"
  ),
  confirmPassword: yup
    .string()
    .required(errorMessages.confirmPassword.required)
    .oneOf([yup.ref("password")], errorMessages.confirmPassword.mismatch),
});

export const forgotPasswordSchema = yup.object().shape({
  email: emailValidation,
});

export const profileEditSchema = yup.object().shape({
  userName: yup
    .string()
    .trim()
    .required(errorMessages.name.required)
    .max(254, "Name is too long")
    .matches(personNameRegex, errorMessages.name.invalidFormat),
  mobile: phoneNumberValidation,
});

export const resetPasswordSchema = yup.object().shape({
  password: passwordValidation,
  confirmPassword: yup
    .string()
    .transform((value) =>
      value === undefined || value === null ? value : String(value).trim()
    )
    .required(errorMessages.confirmPassword.required)
    .oneOf([yup.ref("password")], errorMessages.confirmPassword.mismatch),
});

export const createListingSchema = yup.object().shape({
  title: yup
    .string()
    .required("Title is required")
    .min(3, "Title must be at least 3 characters")
    .max(100, "Title cannot exceed 100 characters"),
  description: yup
    .string()
    .required("Description is required")
    .min(10, "Description must be at least 10 characters")
    .max(1000, "Description cannot exceed 1000 characters"),
  category: yup.object().shape({
    value: yup.string().required("Category is required"),
    label: yup.string().required(),
  }).required("Category is required"),
  condition: yup.object().shape({
    value: yup.string().required("Condition is required"),
    label: yup.string().required(),
  }).required("Condition is required"),
  address: yup.string().required("Address is required"),
  lat: yup.number().nullable().required("Latitude is required"),
  lon: yup.number().nullable().required("Longitude is required"),
  uploadedImages: yup
    .array()
    .min(1, "At least one image is required")
    .max(10, "Maximum 10 images allowed"),
});

export const reportSchema = yup.object().shape({
  category: yup.string().required("Please select a report category"),
  description: yup
    .string()
    .required("Description is required")
    .min(10, "Description must be at least 10 characters")
    .max(1000, "Description cannot exceed 1000 characters"),
  images: yup.array().max(10, "Maximum 10 images allowed"),
});


export function validationErrorToFormikErrors(
  error: ValidationError
): Record<string, string> {
  const errors: Record<string, string> = {};
  if (error.inner?.length) {
    for (const err of error.inner) {
      if (err.path) {
        errors[err.path] = err.message;
      }
    }
  } else if (error.path) {
    errors[error.path] = error.message;
  }
  return errors;
}

export const alertSchema = yup.object().shape({
  submitMode: yup
    .string()
    .oneOf(["targeted", "broadcast"])
    .required("Submit mode is required"),
  userId: yup.string().when("submitMode", {
    is: "targeted",
    then: (schema) => schema.trim().required("Target user ID is required"),
    otherwise: (schema) => schema.notRequired(),
  }),
  title: yup.string().required("Title is required"),
  message: yup.string().required("Message is required"),
  level: yup.string().required("Level is required"),
});
